import { useRunActionsOnAgendaRecordMutation, useUpdateAgendaRecordMutation } from 'store/services/flow/agendaRecords';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { convertObjectToArray } from 'utils/agendaRecord/record';
import { toLowerCamelCase } from 'utils/string';
import { getButtonFormDefinition } from 'utils/userAgendaTableTypeGuard';
import { processFormAttributes } from 'utils/table/user/contextMenu';
import { handleRedirectActions } from 'utils/agendaRecord/handleRedirectActions';
import { validateMessage } from 'utils/agendaRecord/validateMessage';
import { handleSaveError } from 'utils/agendaRecord/handleSaveError';
import { isMobileApp } from 'utils/common';

import type { AgendaRecordItem, DataSchema, FormItemDefinition, GetAgendaRecordsRes, UpdateAgendaRecordRes } from 'types';
import { CHANGE_BEHAVIOR_OPTION, TABLE_CELL_TYPE } from 'types/enums';
import type { ButtonBehaviorModalWithRows } from 'types/modals';
import type { FormItemDefinitionWithButton } from 'types/formItem';

export type ContextMenuItem = { name: string; changeBehavior: (rowId: string) => void; showButtonDirectly?: boolean };

export type ButtonsGroup = {
  formItemId: string;
  formItemDefinition: FormItemDefinition;
  showInToolbar?: boolean;
  order?: number;
}[];

export interface UseAgendaContextMenuProps {
  dataSchema: DataSchema;
  selectedRows?: string[];
  agendaId: string;
  data?: GetAgendaRecordsRes<AgendaRecordItem[]>;
  onRemoveRecords?: (recordIds: string[]) => void;
}

export interface UseAgendaContextMenuReturn {
  contextMenuItems: ContextMenuItem[];
  toolbarItems: ContextMenuItem[];
  modalState: ButtonBehaviorModalWithRows;
  closeModal: () => void;
  handleConditionRunConfirm: () => Promise<void>;
  isActionLoading: boolean;
}

export const useAgendaContextMenu = ({
  agendaId,
  dataSchema,
  selectedRows,
  data,
  onRemoveRecords,
}: UseAgendaContextMenuProps): UseAgendaContextMenuReturn => {
  const { t } = useTranslation();
  const [updateAgendaRecord, { isLoading: isUpdateLoading }] = useUpdateAgendaRecordMutation();
  const [runActionsOnAgendaRecord, { isLoading: isRunActionsLoading }] = useRunActionsOnAgendaRecordMutation();
  const navigate = useNavigate();
  const mobileApp = isMobileApp();

  type FormItem = {
    formItemId: string;
    // eslint-disable-next-line
    buttonAttributeInput?: { [key: string]: any } | null;
    // eslint-disable-next-line
    [key: string]: any;
  };

  const [modalState, setModalState] = useState<ButtonBehaviorModalWithRows>({
    open: false,
    buttonBehavior: CHANGE_BEHAVIOR_OPTION.NONE,
    buttonId: undefined,
    rowId: undefined,
    selectedRowsIds: undefined,
  });

  const closeModal = useCallback(
    () =>
      setModalState({ open: false, buttonBehavior: CHANGE_BEHAVIOR_OPTION.NONE, buttonId: undefined, rowId: undefined, selectedRowsIds: undefined }),
    []
  );

  const filterItems = useCallback((items: FormItem[]): FormItem[] => {
    const grouped = items.reduce<Record<string, FormItem[]>>((acc, item) => {
      acc[item.formItemId] = acc[item.formItemId] || [];
      acc[item.formItemId].push(item);
      return acc;
    }, {});

    return Object.values(grouped).map((group) => {
      const withButton = group.find((i) => i.buttonAttributeInput !== null && i.buttonAttributeInput !== undefined);
      return withButton ?? group[0];
    });
  }, []);

  const getRemovedRecords = useCallback((responses: UpdateAgendaRecordRes[]) => {
    const removedRecordIds: string[] = [];

    responses.forEach((res) => {
      if (res.deleteRecordAction?.deletedRecords) {
        removedRecordIds.push(...res.deleteRecordAction.deletedRecords);
      }
    });

    return removedRecordIds;
  }, []);

  const getFormAttributes = useCallback(
    (changeBehavior: string | undefined, items: AgendaRecordItem[] | undefined, buttonFormItemId: string, rowId: string | undefined) => {
      const onlyActionInput = changeBehavior === CHANGE_BEHAVIOR_OPTION.RUN_ACTIONS_WITHOUT_SAVING;

      return processFormAttributes(items, buttonFormItemId, rowId, onlyActionInput);
    },
    []
  );

  const performAction = useCallback(
    async (rowId: string | undefined, changeBehavior: string | undefined, attributes: FormItem[]) => {
      const reqData = {
        agendaId,
        body: {
          status: 'Active',
          attributes,
        },
        recordId: rowId,
      };

      if (changeBehavior === CHANGE_BEHAVIOR_OPTION.RUN_ACTIONS_WITHOUT_SAVING) {
        return runActionsOnAgendaRecord(reqData).unwrap();
      } else {
        return updateAgendaRecord(reqData).unwrap();
      }
    },
    [agendaId, runActionsOnAgendaRecord, updateAgendaRecord]
  );

  const runButtonAction = useCallback(
    async (rowId: string | undefined, selectedRowsIds: string[] = [], buttonFormItemId: string, changeBehavior?: string) => {
      const { items } = data ?? {};

      try {
        if (Array.isArray(selectedRowsIds) && selectedRowsIds.length > 0) {
          const res = await Promise.all(
            selectedRowsIds.map((rowIdentifier) => {
              const attributes = getFormAttributes(changeBehavior, items, buttonFormItemId, rowIdentifier);
              return performAction(rowIdentifier, changeBehavior, filterItems(attributes));
            })
          );

          onRemoveRecords?.(getRemovedRecords(res));
          validateMessage(res);
          handleRedirectActions(res, navigate);
        } else {
          const attributes = getFormAttributes(changeBehavior, items, buttonFormItemId, rowId);
          const res = await performAction(rowId, changeBehavior, attributes);

          onRemoveRecords?.(getRemovedRecords([res]));
          validateMessage(res);
          handleRedirectActions(res, navigate);
        }

        toast.success(t('toast.updated.record'));
      } catch (error) {
        handleSaveError(error, {
          onActionError: () => toast.error(t('toast.error.action')),
          onUnknownError: () => toast.error(t('common.error')),
        });
      }
    },
    [data, filterItems, getFormAttributes, getRemovedRecords, navigate, performAction, t, onRemoveRecords]
  );

  const getButtonAction = useCallback(
    (rowId: string | undefined, selectedRowsIds: string[] = [], buttonFormItemId: string, changeBehavior?: string) => {
      return (async (): Promise<void> => {
        if (changeBehavior === CHANGE_BEHAVIOR_OPTION.RUN_ACTIONS_WITHOUT_SAVING) {
          setModalState({ open: true, buttonBehavior: changeBehavior, buttonId: buttonFormItemId, rowId, selectedRowsIds });
          return;
        }

        runButtonAction(rowId, selectedRowsIds, buttonFormItemId, changeBehavior);
      })();
    },
    [runButtonAction]
  );

  const handleConditionRunConfirm = useCallback(async () => {
    const { buttonBehavior, buttonId, rowId, selectedRowsIds } = modalState;
    if (!buttonId) return;
    await runButtonAction(rowId, selectedRowsIds, buttonId, buttonBehavior);
    closeModal();
  }, [closeModal, modalState, runButtonAction]);

  const buttonsGroup: ButtonsGroup = useMemo(() => {
    const buttons =
      convertObjectToArray(dataSchema)?.filter((item) => [TABLE_CELL_TYPE.HELIOS_BUTTON, TABLE_CELL_TYPE.BUTTON].includes(item.type)) ?? [];

    return (
      buttons.flatMap(({ formItemDefinition, label, showInToolbar, order }) => {
        if (!formItemDefinition) return [];
        return [
          {
            formItemDefinition,
            formItemId: label,
            showInToolbar,
            order,
          },
        ];
      }) ?? []
    );
  }, [dataSchema]);

  const { contextMenuItems, toolbarItems } = useMemo(() => {
    const toMenuItem = ({ formItemId, formItemDefinition }: ButtonsGroup[number]): ContextMenuItem => {
      const defKey = toLowerCamelCase(formItemDefinition.type) as keyof FormItemDefinition;
      const { name } = getButtonFormDefinition(formItemDefinition, defKey);

      return {
        name,
        showButtonDirectly: (formItemDefinition as FormItemDefinitionWithButton).button?.showButtonDirectly ?? false,
        changeBehavior: (rowId?: string) =>
          getButtonAction(rowId, selectedRows, formItemId, (formItemDefinition as FormItemDefinitionWithButton).button?.changeBehavior),
      };
    };

    if (mobileApp) {
      const allItems = buttonsGroup.sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER)).map(toMenuItem);

      return { contextMenuItems: allItems, toolbarItems: [] };
    }

    const contextItems: ContextMenuItem[] = [];
    const toolbarButtons: ButtonsGroup = [];

    buttonsGroup.forEach((button) => {
      if (button.showInToolbar) {
        toolbarButtons.push(button);
      } else {
        contextItems.push(toMenuItem(button));
      }
    });

    const toolbar = toolbarButtons.sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER)).map(toMenuItem);

    return { contextMenuItems: contextItems, toolbarItems: toolbar };
  }, [buttonsGroup, getButtonAction, mobileApp, selectedRows]);

  return {
    contextMenuItems,
    toolbarItems,
    modalState,
    closeModal,
    handleConditionRunConfirm,
    isActionLoading: isUpdateLoading || isRunActionsLoading,
  };
};
